import { styled, SxProps } from '@mui/material'
import clsx from 'clsx'
import {
  ForwardedRef,
  forwardRef,
  MouseEvent,
  RefObject,
  SyntheticEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { StoryPeerType } from '@weblite/ts-action/story'
import { getFileUrl } from '@weblite/file'
import { animated } from '@react-spring/web'
import { useToggle } from 'usehooks-ts'
import { useHistoryCustomBack } from 'weblite-router'
import {
  usePeerStories,
  useUploadingStoriesByPeerIdAndPeerType,
} from '$logic/story/react-hooks'
import { deleteStories, viewStory } from '$logic/story/service'
import { useLongPress } from '$hooks/use-long-press'
import { preventDefault } from '$helper/functions/prevent-default'
import { SEVideo, StoryContent, StoryRibbon } from '$logic/story/types'
import { getClientUserId } from '$logic/client-user'
import { ConfirmationDialog } from '$comp/modals/confirmation-dialog'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { blackToTransparentGradient } from '$setup/style/helpers'
import useEffectWithPrevDeps from '$hooks/use-effect-with-prev-deps'
import { StoryAnalyticsApi } from '$logic/story/analytics'
import { IS_SAFARI } from '$helper/functions/environmentInfo'
import useFlag from '$hooks/use-flag'
import { round } from '$helper/functions/math'
import useBackgroundMode from '$hooks/window/use-background-mode'
import captureKeyboardEvents from '$helper/functions/capture-keyboard-events'
import { nothing } from '$func/utils'
import { t } from '$i18n'
import { useStateRef } from '$hooks/use-state-ref'
import { useLastCallback } from '$hooks/use-last-callback'
import { HallPeerInfo, UserPeerInfo } from './peer-info'
import StoryProgress from '../story-progress'
import { LoadingStories } from './loading'
import { Retry } from './retry'
import { ActionButtons } from './action-buttons'
import { StatusBar } from './status-bar'
import { ViewersList } from './viewers-list'
import { StoryImage } from './story-image'
import { OptimizedVideo } from './optimized-video'
import { useStoryPauseController } from './use-story-pause-controller'
import { useStoryDragController } from './use-story-drag-controller'

export interface StoryViewerActionsApi {
  onBack: () => void
  onForward: () => void
}

const TAP_LIMIT = 0.45
const VIDEO_MIN_READY_STATE = IS_SAFARI ? 4 : 3

interface Props {
  storyRibbon: StoryRibbon
  isActive: boolean
  isSliderMovingRef?: RefObject<boolean>
  sx?: SxProps
  desktopMode?: boolean
  goToNextSlide: () => unknown
  goToPrevSlide: () => unknown
  isModalOpen: boolean
  onClose: () => void
  onCreateStory: (peerId: string, peerType: StoryPeerType) => void
}

export const PeerStoryViewer = forwardRef(function PeerStoryViewerComponent(
  {
    storyRibbon,
    isActive: isSlideActive,
    isSliderMovingRef,
    sx,
    desktopMode = false,
    goToNextSlide,
    goToPrevSlide,
    isModalOpen,
    onClose,
    onCreateStory,
  }: Props,
  ref: ForwardedRef<StoryViewerActionsApi>,
) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [videoDuration, setVideoDuration] = useState<number>()

  const { peerId, peerType } = storyRibbon
  const [updateIndexTrigger, triggerUpdateCurrentIndex] = useToggle()

  const [isMediaLoading, markIsMediaLoading, unMarkIsMediaLoading] =
    useFlag(false)
  const [viewersListIsOpen, setViewersListIsOpen] = useState(false)
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] =
    useState(false)

  const [muted, toggleMuted] = useToggle(false)

  const isPeerHall = peerType === 'CHANNEL' || peerType === 'GROUP'
  const isPeerUser = peerType === 'USER'

  const {
    stories,
    isLoading: isLoadingStories,
    hasError,
    retry: retryLoadingStories,
  } = usePeerStories({
    storyRibbon,
    isSlideActive,
    // TODO[review-story]: check if this can be replaced with ()=>calculateCurrentIndex()
    onLoadStories: triggerUpdateCurrentIndex,
  })

  const uploadingStory = useUploadingStoriesByPeerIdAndPeerType(
    peerId,
    peerType,
  )

  const isUploadingNewStory = !!uploadingStory?.uploadId
  const isInUploadingIndex =
    isUploadingNewStory && currentIndex === (stories?.length || 0)

  const {
    isStoryPlaying,
    isPlaybackRequested: isStoryPlaybackRequested,
    videoPlayerRef,

    isPausedByModal,
    isPausedByLongPress,

    markIsPausedByLongPress,
    unMarkIsPausedByLongPress,
    toggleIsPausedBySpaceBar,
    unMarkIsPausedBySpaceBar,
    markIsPausedByBlur,
    unMarkIsPausedByBlur,
    markIsPausedByModal,
    unMarkIsPausedByModal,
  } = useStoryPauseController({
    isLoadingStories,
    isMediaLoading,
    isSlideActive,
    hasError,
    uploading: isInUploadingIndex,
  })

  const { offsetY, bindGestureEvents } = useStoryDragController({
    onClose,
    enabled: !desktopMode && !isPausedByModal,
    isSliderMovingRef,
  })

  const currentStory = stories?.[currentIndex]

  const currentStoryContent = (
    currentStory?.content as StoryContent | undefined
  )?.[0]
  const currentStoryContentRef = useStateRef(currentStoryContent)

  const currentMediaIsImage = isInUploadingIndex
    ? uploadingStory?.type === 'IMAGE'
    : currentStoryContent?.type === 'IMAGE'

  const currentMediaIsVideo = isInUploadingIndex
    ? uploadingStory?.type === 'VIDEO'
    : currentStoryContent?.type === 'VIDEO'

  const uploadingFileUrl = uploadingStory?.fileUrl

  useBackgroundMode(markIsPausedByBlur, unMarkIsPausedByBlur)

  const fileUrl = useMemo(() => {
    if (currentStoryContent?.fileUrl) return currentStoryContent.fileUrl
    if (isInUploadingIndex) return uploadingFileUrl
    return getFileUrl(currentStoryContent?.fileId || '')
  }, [currentStoryContent, isInUploadingIndex, uploadingFileUrl])

  const handleView = useCallback(() => {
    const currentStoryViewed = !!storyRibbon.stories?.find(
      story => story.id === currentStory?.id,
    )?.viewedAt

    if (
      !isSlideActive ||
      !isModalOpen ||
      !currentStory?.id ||
      currentStoryViewed
    ) {
      return
    }

    viewStory(peerId, peerType, currentStory.id)
  }, [
    isSlideActive,
    peerId,
    peerType,
    currentStory,
    storyRibbon,
    currentIndex,
    isModalOpen,
  ])

  const handleViewRef = useStateRef(handleView)

  //  resetting video when opening previously opened video
  useEffect(() => {
    if (videoPlayerRef.current && isSlideActive) {
      videoPlayerRef.current.currentTime = 0
    }
  }, [isSlideActive, currentMediaIsVideo])

  const storyLength = useMemo(() => {
    const nonUploadingStoriesLength = stories?.length || 0

    return isUploadingNewStory
      ? nonUploadingStoriesLength + 1
      : nonUploadingStoriesLength
  }, [isUploadingNewStory, stories])

  const storyLengthRef = useStateRef(storyLength)

  const calculateCurrentIndex = useCallback(() => {
    if (isUploadingNewStory) {
      setCurrentIndex(storyLength - 1)
      return
    }

    // This search is needed because stories in storyRibbon are not sorted based on story date
    const firstUnseenStoryIndex = stories?.findIndex(story => {
      const viewedAt = storyRibbon.stories?.find?.(
        s => s.id === story.id,
      )?.viewedAt
      return !viewedAt
    })
    if (firstUnseenStoryIndex === -1 || firstUnseenStoryIndex === undefined) {
      return
    }

    setCurrentIndex(firstUnseenStoryIndex)
  }, [stories, storyRibbon.stories, storyLength, isUploadingNewStory])

  const goToPrevSlideRef = useStateRef(goToPrevSlide)
  const goToNextSlideRef = useStateRef(goToNextSlide)

  const handleOpenNextStory = useLastCallback(() => {
    if (!storyLengthRef.current) return

    setCurrentIndex(index => {
      unMarkIsPausedBySpaceBar()
      if (index === storyLengthRef.current - 1) {
        goToNextSlideRef.current()
        return index
      }

      unMarkIsMediaLoading()
      StoryAnalyticsApi.swipe('NEXT', 'TAP')
      return index + 1
    })
  })

  const handleOpenPreviousStory = useLastCallback(() => {
    setCurrentIndex(index => {
      unMarkIsPausedBySpaceBar()
      if (index === 0) {
        goToPrevSlideRef.current()
        return index
      }

      unMarkIsMediaLoading()
      StoryAnalyticsApi.swipe('PREVIOUS', 'TAP')
      return index - 1
    })
  })

  useEffectWithPrevDeps(
    ([prevPeerId, prevPeerType, prevUpdateIndexTrigger]) => {
      if (
        prevPeerId === peerId &&
        prevPeerType === peerType &&
        updateIndexTrigger === prevUpdateIndexTrigger
      )
        return
      calculateCurrentIndex()
    },
    [peerId, peerType, updateIndexTrigger, calculateCurrentIndex, storyLength],
  )

  useImperativeHandle(
    ref,
    () => ({
      onBack: handleOpenPreviousStory,
      onForward: handleOpenNextStory,
    }),
    [handleOpenPreviousStory, handleOpenNextStory],
  )

  const handleTap = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (desktopMode) return
      const clientX =
        'pageX' in e
          ? e.pageX
          : 'touches' in e
            ? e.touches[0]?.pageX
            : undefined
      if (!clientX) return
      const rect =
        (e.target as HTMLElement)?.getBoundingClientRect() ||
        e.currentTarget.getBoundingClientRect()

      const x = (clientX - rect.left) / rect.width

      if (x > 1 - TAP_LIMIT) handleOpenNextStory()
      if (x < TAP_LIMIT) handleOpenPreviousStory()
    },
    [handleOpenPreviousStory, handleOpenNextStory, desktopMode],
  )

  const longPressEvents = useLongPress(
    markIsPausedByLongPress,
    unMarkIsPausedByLongPress,
    handleTap,
    false,
  )

  const handleToggleMuted = useCallback(() => {
    if (!videoPlayerRef.current) return
    toggleMuted()
    videoPlayerRef.current.muted = !videoPlayerRef.current.muted
    StoryAnalyticsApi.toggleMute(muted)
  }, [])

  const userPermissions = useClientUserPermissions(currentStory?.peerId || '')

  const isAdmin = useMemo(() => {
    if (isPeerHall)
      return Boolean(userPermissions?.isOwner || userPermissions?.isAdmin)

    return (
      currentStory?.peerType === 'USER' &&
      currentStory.peerId === getClientUserId()
    )
  }, [isPeerHall, currentStory, userPermissions])

  const handleDeleteStory = useCallback(async () => {
    handleCloseDeleteConfirmationModal()
    if (storyLength !== 1 && currentIndex === storyLength - 1) {
      setCurrentIndex(storyLength - 2)
    }
    if (currentStory?.id) deleteStories(peerId, peerType, currentStory.id)
  }, [peerId, peerType, currentStory, storyLength, goToNextSlide])

  const handleShowViewersList = useCallback(() => {
    setViewersListIsOpen(true)
    markIsPausedByModal()
    StoryAnalyticsApi.viewersListClick(
      currentMediaIsVideo ? 'VIDEO' : 'IMAGE',
      peerType,
    )
  }, [markIsPausedByModal, currentMediaIsVideo, peerType])

  const handleShowDeleteConfirmationModal = useCallback(async () => {
    setTimeout(() => {
      setDeleteConfirmationIsOpen(true)
      markIsPausedByModal()
    }, 0)
  }, [markIsPausedByModal])

  const handleVideoStoryTimeUpdate = useLastCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      const video = e.currentTarget
      const duration =
        (currentStoryContentRef.current as SEVideo)?.duration || video.duration // TODO: Remove video.duration
      if (duration) setVideoDuration(duration)
      if (video.readyState >= VIDEO_MIN_READY_STATE) {
        handleViewRef.current()
        unMarkIsMediaLoading()
      }
      if (duration && round(video.currentTime, 2) >= round(duration, 2)) {
        handleOpenNextStory()
      }
    },
  )

  useEffect(() => {
    if (!isSlideActive || isPausedByModal) return

    return captureKeyboardEvents({
      onRight: handleOpenNextStory,
      onLeft: handleOpenPreviousStory,
      onSpaceBar: toggleIsPausedBySpaceBar,
      onEsc: onClose,
    })
  }, [
    handleOpenNextStory,
    handleOpenPreviousStory,
    isSlideActive,
    isPausedByModal,
    toggleIsPausedBySpaceBar,
    onClose,
  ])

  const handleCloseViewersList = useCallback(() => {
    setViewersListIsOpen(false)
    unMarkIsPausedByModal()
  }, [unMarkIsPausedByModal])

  const handleCloseDeleteConfirmationModal = useCallback(() => {
    setDeleteConfirmationIsOpen(false)
    unMarkIsPausedByModal()
  }, [unMarkIsPausedByModal])

  const handleDeleteCLickInViewersList = useCallback(() => {
    handleCloseViewersList()
    handleShowDeleteConfirmationModal()
  }, [handleShowDeleteConfirmationModal, handleCloseViewersList])

  useHistoryCustomBack(() => {
    if (viewersListIsOpen) handleCloseViewersList()
    else if (deleteConfirmationIsOpen) handleCloseDeleteConfirmationModal()
    else onClose()
  }, [
    viewersListIsOpen,
    deleteConfirmationIsOpen,
    handleCloseViewersList,
    handleCloseDeleteConfirmationModal,
  ])

  const handleCreateStory = useCallback(() => {
    onCreateStory(peerId, peerType)
  }, [onCreateStory, peerId, peerType])

  return (
    <Root
      onContextMenu={preventDefault}
      sx={{
        ...(desktopMode && !isSlideActive && { cursor: 'pointer' }),
        ...sx,
      }}
    >
      <GestureCapture
        onContextMenu={preventDefault}
        style={{ y: offsetY, touchAction: 'none' }}
        {...bindGestureEvents()}
        sx={{
          ...(desktopMode && !isSlideActive && { pointerEvents: 'none' }),
          borderRadius: desktopMode ? (isSlideActive ? '12px' : '9px') : 0,
          overflow: 'hidden',
        }}
      >
        <StoryContentComponent {...longPressEvents}>
          {currentMediaIsImage ? (
            <StoryImage
              isLoading={isMediaLoading}
              fileUrl={fileUrl}
              thumbnail={currentStoryContent?.thumbnail}
              onLoadingEnded={unMarkIsMediaLoading}
              onLoadingStarted={markIsMediaLoading}
              onViewRef={handleViewRef}
              isUploading={isInUploadingIndex}
            />
          ) : currentMediaIsVideo ? (
            <StoryVideo
              key={`video-${currentStory?.id}`}
              playsInline
              draggable={false}
              disablePictureInPicture
              disableRemotePlayback
              ref={videoPlayerRef}
              src={fileUrl}
              muted={muted}
              autoPlay
              canPlay={isStoryPlaybackRequested}
              onWaiting={markIsMediaLoading}
              onTimeUpdate={handleVideoStoryTimeUpdate}
            />
          ) : null}
        </StoryContentComponent>
        <Header className={clsx({ hidden: isPausedByLongPress })}>
          <ProgressBar>
            {storyRibbon.stories?.map?.((story, index) => (
              <StoryProgress
                key={story.id + peerId + peerType}
                isActive={
                  index === currentIndex && isSlideActive && !isLoadingStories
                }
                isPaused={!isStoryPlaying || hasError}
                isViewed={index < currentIndex}
                onImageComplete={handleOpenNextStory}
                duration={currentMediaIsVideo ? videoDuration : undefined}
                isVideo={currentMediaIsVideo}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
            {isUploadingNewStory && (
              <StoryProgress
                key={uploadingStory?.uploadId + peerId + peerType}
                isActive={
                  isInUploadingIndex && isSlideActive && !isLoadingStories
                }
                isPaused={true}
                isViewed={false}
                onImageComplete={nothing}
                duration={undefined}
              />
            )}
          </ProgressBar>
          {isPeerHall && (
            <HallPeerInfo
              peerId={peerId}
              peerType={peerType}
              updatedAt={
                isInUploadingIndex ? new Date() : currentStory?.updatedAt
              }
              // onDelete={() =>
              onDelete={handleShowDeleteConfirmationModal}
              onToggleMuted={handleToggleMuted}
              loading={isMediaLoading || isLoadingStories}
              canDelete={isAdmin}
              onCloseModal={unMarkIsPausedByModal}
              onOpenModal={markIsPausedByModal}
              isVideo={currentMediaIsVideo}
              isMuted={muted}
            />
          )}
          {isPeerUser && (
            <UserPeerInfo
              peerId={peerId}
              peerType={peerType}
              updatedAt={
                isInUploadingIndex ? new Date() : currentStory?.updatedAt
              }
              onDelete={handleShowDeleteConfirmationModal}
              // onDelete={() =>
              isMuted={muted}
              loading={isMediaLoading || isLoadingStories}
              onToggleMuted={handleToggleMuted}
              canDelete={isAdmin}
              onCloseModal={unMarkIsPausedByModal}
              onOpenModal={markIsPausedByModal}
              isVideo={currentMediaIsVideo}
            />
          )}
        </Header>

        <LoadingStories
          isLoading={
            (isMediaLoading || isLoadingStories) && !isInUploadingIndex
          }
        />
        <Retry show={hasError} onRetry={retryLoadingStories} />
        {!isUploadingNewStory && (
          <ActionButtons
            views={currentStory?.viewCount}
            isPeerHall={isPeerHall}
            hidden={isPausedByLongPress}
            onCreateStory={handleCreateStory}
            openViewersList={handleShowViewersList}
            isStoryAdmin={isAdmin}
          />
        )}
        <ViewersList
          peerId={peerId}
          peerType={peerType}
          storyId={currentStory?.id}
          isOpen={viewersListIsOpen}
          onClose={handleCloseViewersList}
          onDelete={handleDeleteCLickInViewersList}
          viewCount={currentStory?.viewCount}
        />
        <ConfirmationDialog
          onCancel={handleCloseDeleteConfirmationModal}
          confirmOption={t('confirmDialog.deleteStory.confirmOption')}
          onConfirm={handleDeleteStory}
          description={t('confirmDialog.deleteStory.description')}
          cancelOption={t('confirmDialog.deleteStory.cancelOption')}
          isOpen={deleteConfirmationIsOpen}
          title={t('confirmDialog.deleteStory.title')}
        />
        {isInUploadingIndex && (
          <StatusBar
            uploadingPercent={uploadingStory.uploadPercent}
            compressionPercent={
              uploadingStory.type === 'VIDEO'
                ? uploadingStory.compressionPercent
                : undefined
            }
            uploadStatus={uploadingStory.uploadStatus}
          />
        )}
      </GestureCapture>
    </Root>
  )
})

const Root = styled('div')`
  width: 100%;
  height: 100%;
  background: transparent;
`

const GestureCapture = styled(animated.div)`
  display: flex;
  flex-direction: column;
  background-color: #000;
  width: 100%;
  height: 100%;
  z-index: 2;
  position: relative;
`

const Header = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 12px;
  justify-content: center;
  box-sizing: border-box;
  height: 74px;
  position: absolute;
  top: 0;
  left: 0;
  gap: 4px;
  background: ${blackToTransparentGradient('bottom')};
  transition: opacity 0.15s ease;
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

const ProgressBar = styled('div')`
  height: 2px;
  display: flex;
  width: 100%;
  /* @noflip */
  direction: ltr;
`

const StoryContentComponent = styled('div')`
  width: 100%;
  height: 100%;
`

const StoryVideo = styled(OptimizedVideo)`
  width: 100%;
  height: 100%;
  border: 0;
`
